import {isNil, isEmpty, cascadingGetIfNotEmpty, cascadingGet} from '@megaphonenpm/mputils/src/utils.js';
import {get} from 'lodash-es';
import {STATE_SCENE} from './constants.js';

export class CascadingSettingsService {
  constructor () {
  }

  /**
   * Returns the default local MediaAsset for the specified mediaType, starting with the
   * playlist setting, then cascading to the deployment setting. Used to determine the background,
   * show, and sponsor media for a given playlist or deployment.
   *
   * Example:
   * backgroundImg.src = getDefaultMediaURI(CLIENT_TYPE_SCREEN, 'background', somePlaylist, someDeploymentSettings);
   *
   * @param mediaType A media type, such as 'show', 'sponsor', 'foreground', 'background', etc.
   * @param clientType One of the client-type contants, such as CLIENT_TYPE_SCREEN or CLIENT_TYPE_CONTROLLER.
   * @param playlist A playlist object.
   * @param deploymentSettings A deployment-settings object.
   * @returns {*} A string URI to the specified media.
   */
  getDefaultMediaURI (mediaType, clientType, playlist, deploymentSettings) {
    let path = `defaults.content.${mediaType}.media.screen.uri`;
    let mediaURI = get(playlist, path);
    if (isEmpty(mediaURI)) {
      mediaURI = get(deploymentSettings, path);
    }
    mediaURI = isNil(mediaURI) || mediaURI === '' ? null : mediaURI;
    return mediaURI;
  }

  /**
   * Returns a Boolean indicating whether votes (survey bars and percent text) should be displayed
   * for the specified state.
   *
   * @param clientType One of either CLIENT_TYPE_SCREEN or CLIENT_TYPE_CONTROLLER.
   * @param scene The scene object for which to retrieve votes visibility.
   * @param dataStore A reference to the MRTS client's data store, which must include .playlist
   *                  and .deploymentSettings properties.
   * @param state One of either STATE_SCENE or STATE_SCENE_RESULT
   * @returns {*} True if votes should be displayed, false if not.
   */
  getVotesVisibility (clientType, scene, dataStore, state) {
    let propertyName = state === STATE_SCENE ? 'scene' : 'scene_result';

    let voteVisibility = get(scene, `presentation.${clientType}.vote_visibility.${propertyName}`, null);
    if (isNil(voteVisibility)) {
      voteVisibility = cascadingGetIfNotEmpty(dataStore,
        [
          `playlist.defaults.presentation.${clientType}.vote_visibility.${propertyName}`,
          `deploymentSettings.defaults.presentation.${clientType}.vote_visibility.${propertyName}`], true);
    }
    return voteVisibility;
  }

  /**
   * Returns a Boolean indicating whether points should be displayed for the specified scene.
   *
   * @param clientType One of either CLIENT_TYPE_SCREEN or CLIENT_TYPE_CONTROLLER.
   * @param scene The scene object for which to retrieve points visibility.
   * @param dataStore A reference to the MRTS client's data store, which must include .playlist
   *                  and .deploymentSettings properties.
   * @returns {*} True if points should be displayed, false if not.
   */
  getPointsVisibility (clientType, scene, dataStore) {
    let pointsVisibility = get(scene, `presentation.${clientType}.points_visibility.scene`, null);
    if (isNil(pointsVisibility)) {
      pointsVisibility = cascadingGetIfNotEmpty(dataStore,
        [
          `playlist.defaults.presentation.${clientType}.points_visibility.scene`,
          `deploymentSettings.defaults.presentation.${clientType}.points_visibility.scene`], true);
    }
    return pointsVisibility;
  }

  /**
   * Returns a Boolean indicating whether a countdown should be displayed for the specified state.
   *
   * @param clientType One of either CLIENT_TYPE_SCREEN or CLIENT_TYPE_CONTROLLER.
   * @param scene The scene object for which to retrieve countdown visibility.
   * @param dataStore A reference to the MRTS client's data store, which must include .playlist
   *                  and .deploymentSettings properties.
   * @param state One of either STATE_SCENE or STATE_SCENE_RESULT
   * @returns {*} True if a countdown should be displayed, false if not.
   */
  getCountdownVisibility (clientType, scene, dataStore, state) {
    let propertyName = state === STATE_SCENE ? 'scene' : 'scene_result';

    let countdownVisibility = get(scene, `presentation.${clientType}.countdown_visibility.${propertyName}`, null);
    if (isNil(countdownVisibility)) {
      countdownVisibility = cascadingGetIfNotEmpty(dataStore,
        [
          `playlist.defaults.presentation.${clientType}.countdown_visibility.${propertyName}`,
          `deploymentSettings.defaults.presentation.${clientType}.countdown_visibility.${propertyName}`], true);
    }
    return countdownVisibility;
  }


  /**
   * Returns the value of the specified "screen presentation setting", at the highest level of
   * precedence (from scene setting, to playlist setting, to project setting).
   *
   * Example:
   * // The code below retrieves the following presentation setting:
   * //   scene.presentation.screen.position
   * // If no value for position is found on the scene, then the playlist setting is consulted:
   * //   playlist.defaults.presentation.screen.position
   * // If no value for position is specified on the playlist, then the project setting is consulted:
   * //   deploymentSettings.defaults.presentation.screen.position
   *
   * // Retrieve highest-precedence value for 'position' setting
   * getScreenPresentationSetting('position', someScene, referenceToDataStore);
   *
   * @param settingName The name of the setting to retrieve.
   * @param scene The scene object for which to retrieve points visibility.
   * @param dataStore A reference to the MRTS client's data store, which must include .playlist
   *                  and .deploymentSettings properties.
   * @returns {*} the value of the specified "presentation setting".
   */
  getScreenPresentationSetting (settingName, scene, dataStore) {
    let settingValue = get(scene, `presentation.screen.${settingName}`, null);
    if (isNil(settingValue)) {
      settingValue = cascadingGetIfNotEmpty(dataStore,
        [
          `playlist.defaults.presentation.screen.${settingName}`,
          `deploymentSettings.defaults.presentation.screen.${settingName}`], null);
    }
    return settingValue;
  }

  /**
   * Returns the most-specific value for the specified fullSettingPath. To determine the
   * most-specific value, getSetting() consults scene settings, then playlist settings,
   * then project settings, in that order. For example, given the following
   * code...
   *
   * getSetting('content.foreground.media.screen.loop', someScene, referenceToDataStore);
   *
   * ...getSetting() returns the first non-null value found in the following locations:
   *  scene.content.foreground.media.screen.loop
   *  dataStore.playlist.defaults.content.foreground.media.screen.loop
   *  dataStore.deploymentSettings.defaults.content.foreground.media.screen.loop
   *
   * To skip empty values during setting retrieval, use getSettingIfNotEmpty().
   *
   * @param fullSettingPath The full path to the setting, including the setting name
   *                        E.g., 'content.foreground.media.screen.loop'
   * @param scene The scene object for which to retrieve the specified setting.
   * @param dataStore A reference to the MRTS client's data store, which must include .playlist
   *                  and .deploymentSettings properties.
   */
  getSetting (fullSettingPath, scene, dataStore) {
    let settingValue = get(scene, fullSettingPath, null);
    if (isNil(settingValue)) {
      settingValue = cascadingGet(dataStore,
        [
          `playlist.defaults.${fullSettingPath}`,
          `deploymentSettings.defaults.${fullSettingPath}`], null);
    }
    return settingValue;
  }

  /**
   * Identical to getSetting(), except that in the cascading search for the most-specific setting
   * value, getSettingIfNotEmpty() skips empty values. Use getSettingIfNotEmpty() to ignore settings
   * with values such as the empty string or an empty array. For example, given the following
   * settings...
   *
   * // Playlist Setting
   * // defaults.content.sponsor.media.screen.uri = ''
   *
   * // Project Setting
   * // defaults.content.sponsor.media.screen.uri = 'logo.jpg'
   *
   *  ...getSetting() would return '' (because although the playlist setting is empty, it is not
   *  null), whereas getSettingIfNotEmpty() would return 'logo.jpg' (because the playlist setting
   *  is empty, so getSettingIfNotEmpty() skips the playlist setting and proceeds to the project
   *  setting, which has a non-empty value).
   */
  getSettingIfNotEmpty (fullSettingPath, scene, dataStore) {
    let settingValue = get(scene, fullSettingPath, null);
    if (isEmpty(settingValue)) {
      settingValue = cascadingGetIfNotEmpty(dataStore,
        [
          `playlist.defaults.${fullSettingPath}`,
          `deploymentSettings.defaults.${fullSettingPath}`], null);
    }
    return settingValue;
  }
}
